class UpsellCarousel extends HTMLElement {
  connectedCallback() {
    const productId = this.dataset.productId;
    const sectionId = this.dataset.sectionId;
    const collectionHandle = this.dataset.collectionHandle;

    // sectionId is required for cart updates; productId can be empty (we fall back to collection/products.json)
    if (!sectionId) return;

    this.renderCarousel(productId || null, sectionId, collectionHandle);
  }

  async renderCarousel(productId, sectionId, collectionHandle) {
    try {
      let products = [];

      // 1. Try custom collection selected in customizer
      if (collectionHandle && collectionHandle.trim() !== '') {
        try {
          const response = await fetch(`/collections/${collectionHandle.trim()}/products.json?limit=8`);
          if (response.ok) {
            const data = await response.json();
            products = data.products || [];
          }
        } catch (e) {
          console.warn(`Custom collection '${collectionHandle}' fetch failed, falling back to recommendations:`, e);
        }
      }

      // 2. Fall back to recommendations API if custom collection is empty or not selected (only if productId is valid)
      if (productId && (!products || products.length === 0)) {
        try {
          const response = await fetch(`/recommendations/products.json?product_id=${productId}&limit=5`);
          if (response.ok) {
            const data = await response.json();
            products = data.products || [];
          }
        } catch (e) {
          console.warn('Recommendations fetch failed, falling back to products.json:', e);
        }
      }

      // 3. Fall back to /products.json if both recommendations and custom collection are empty
      if (!products || products.length === 0) {
        try {
          const fallbackRes = await fetch('/products.json?limit=8');
          if (fallbackRes.ok) {
            const fallbackData = await fallbackRes.json();
            products = fallbackData.products || [];
          }
        } catch (e) {
          console.error('Fallback products fetch failed:', e);
        }
      }

      if (!products || products.length === 0) return;

      // 4. Normalize products — handle both /products.json and /recommendations/products.json response shapes
      const normalizedProducts = products.map(product => {
        const variant = product.variants && product.variants[0];
        if (!variant) return null;

        // Price: /products.json uses cents integer on product.price; recommendations API uses string on variant.price
        let price = '0.00';
        if (product.price !== undefined && typeof product.price === 'number') {
          price = (product.price / 100).toFixed(2);
        } else if (variant.price !== undefined) {
          const p = parseFloat(variant.price);
          price = isNaN(p) ? '0.00' : p.toFixed(2);
        }

        // Image: featured_image can be a URL string (/products.json) or an object with .src (/recommendations)
        let image = '';
        if (product.featured_image) {
          if (typeof product.featured_image === 'string') {
            image = product.featured_image;
          } else if (product.featured_image.src) {
            image = product.featured_image.src;
          }
        }
        if (!image && product.images && product.images.length > 0) {
          const imgObj = product.images[0];
          image = typeof imgObj === 'string' ? imgObj : (imgObj.src || '');
        }
        // Ensure the image URL has a scheme
        if (image && image.startsWith('//')) image = 'https:' + image;

        const url = product.url || `/products/${product.handle}`;

        return {
          id: product.id,
          title: product.title,
          url: url,
          image: image,
          variantId: variant.id,
          price: price,
          available: variant.available !== false
        };
      }).filter(p => p !== null && p.available);

      // 5. Filter out the current product (productId) to avoid upselling what is already in cart
      const filteredProducts = productId
        ? normalizedProducts.filter(p => p.id.toString() !== productId.toString())
        : normalizedProducts;

      // 6. Limit to 4 items
      const finalProducts = filteredProducts.slice(0, 4);

      if (finalProducts.length === 0) return;

      this.innerHTML = `
        <div class="upsell-carousel">
          <h3 class="upsell-carousel__title">You May Also Like</h3>
          <div class="upsell-carousel__track">
            ${finalProducts.map(product => `
                <div class="upsell-card" data-variant-id="${product.variantId}">
                  <a href="${product.url}" class="upsell-card__image-link">
                    <div class="upsell-card__image-wrapper">
                      <img class="upsell-card__image" src="${product.image ? product.image + '&width=200' : ''}" alt="${product.title}" loading="lazy" width="150" height="150">
                    </div>
                  </a>
                  <a href="${product.url}" class="upsell-card__title">${product.title}</a>
                  <div class="upsell-card__price">$${product.price}</div>
                  <button class="btn btn-small btn-primary upsell-card__button" type="button" data-variant-id="${product.variantId}">
                    <span>Add</span>
                    <svg class="icon-plus hidden" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="12" height="12">
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                  </button>
                </div>
              `).join('')}
          </div>
        </div>
      `;

      // Set up click handlers
      this.querySelectorAll('.upsell-card__button').forEach(btn => {
        btn.addEventListener('click', (e) => this.handleAddToCart(e, btn, sectionId));
      });

    } catch (e) {
      console.error('Failed to render upsell carousel:', e);
    }
  }

  async handleAddToCart(e, button, sectionId) {
    e.preventDefault();
    if (button.disabled) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    // Show loading state
    button.disabled = true;
    const btnText = button.querySelector('span');
    if (btnText) btnText.textContent = 'Adding...';

    try {
      // Find all sections that need to be updated (like cart-items-component targets)
      const sectionsToUpdate = new Set([sectionId]);
      document.querySelectorAll('cart-items-component').forEach((item) => {
        if (item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      const body = JSON.stringify({
        id: Number(variantId),
        quantity: 1,
        sections: Array.from(sectionsToUpdate).join(',')
      });

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: body
      });

      if (!response.ok) throw new Error('Add to cart failed');

      const cartData = await response.json();

      // Dispatch event to trigger the main cart items update
      const updateEvent = new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cartData,
          sourceId: 'upsell-carousel',
          data: {
            sections: cartData.sections
          }
        }
      });
      this.dispatchEvent(updateEvent);

    } catch (err) {
      console.error('Upsell add to cart error:', err);
      if (btnText) btnText.textContent = 'Error';
      setTimeout(() => {
        button.disabled = false;
        if (btnText) btnText.textContent = 'Add';
      }, 2000);
    }
  }
}

if (!customElements.get('upsell-carousel')) {
  customElements.define('upsell-carousel', UpsellCarousel);
}
